import React, { useRef, useState } from "react";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import BaseModel from "../../models/BaseModel";
import { hideToast, showToast } from "../Toasts";
import { itx } from "../../translationItx/itx";
import { EventKey } from "../../eventLogger/EventKeys";
import type Button from "../../models/Button";
import type Group from "../../models/Group";
import type Room from "../../models/Room";

type AddSearchObject = (object: BaseModel) => void;

/** Adds a button and its searchable channel hierarchy. */
function addButtonHierarchy(button: Button, addObject: AddSearchObject): void {
  addObject(button);
  for (const channel of button.channels) {
    addObject(channel);
    for (const signal of channel.signals) {
      addObject(signal);
    }
  }
}

/** Adds a group and all nested electrical objects recursively. */
function addGroupHierarchy(group: Group, addObject: AddSearchObject): void {
  addObject(group);

  for (const consumer of group.consumers) {
    addObject(consumer);
  }
  for (const button of group.buttons) {
    addButtonHierarchy(button, addObject);
  }
  for (const childGroup of group.groups) {
    addGroupHierarchy(childGroup, addObject);
  }
}

/** Adds the complete searchable electrical hierarchy of a room. */
function addRoomHierarchy(room: Room, addObject: AddSearchObject): void {
  addObject(room);

  for (const consumer of room.consumers) {
    addObject(consumer);
  }
  for (const button of room.buttons) {
    addButtonHierarchy(button, addObject);
  }
  for (const group of room.groups) {
    addGroupHierarchy(group, addObject);
  }
}

// Key Information regarding Project Tree Search:
// ===============================================
// Search Mechanism used (in sequential order):
// -  by obj.name
// -  by obj.tag
// -  for channel: by knxChannelId
// -  for signal: by knxFunctionText
// -  Visible text search (using prefix / postfix / fullTitle)
//
// Reference nodes (generally leaf nodes) are not searchable
// ( there is object.id mismatch between actual leaf node shown in hierarchy and one accessed through
// treeViewModel childCollectionEx, hence unable to auto-expand and navigate to such nodes)
//
// Nodes that are not searchable at present:
// -  GroupAddressReference inside Consumers / Buttons / Channel Signal under Floors/Cabinets
// -  DeviceReference under Products
// -  BaseModelReference (DeviceReference) inside lvl3Address under KnxAddresses / Scene
// -  Room Reference inside RoomPlans under Documents
// -  BaseModelReference (DeviceReference) inside Topologies
//
// All the other tree nodes are searchable by their respective visible text in project tree.

export default function SearchBar() {
  const searchCollection = useRef(new Array<BaseModel>());
  const searchCollectionIndex = useRef(0);
  const searchExpression = useRef("");
  const [uiChangeFlag, setUiChangeFlag] = useState(0);

  useOnChangeWatcher(state.treeSearchState, () => {
    setTimeout(() => {
      if (state.treeSearchState.value) {
        if (refInput.current) {
          refInput.current.focus();
        }
      }
    }, 100);

    return true;
  });

  const refInput = useRef<HTMLInputElement>(null);

  async function handleSearch() {
    const searchString = refInput.current?.value.toLocaleLowerCase();
    const nextSearchCollection = new Array<BaseModel>();

    if (searchString && searchString.length > 1) {
      state.eventLogger.addEvent(EventKey.NavigatorSearched, {
        searchTerm: searchString,
      });
      function addToSearchCollection(obj: BaseModel) {
        // If Object already in searchCollection, return;
        // const filteredObjects = nextSearchCollection.filter((filteredObj) => filteredObj.id == obj.id);
        // if (filteredObjects.length > 0) return;

        if (searchString) {
          // Search by Tag

          if (!obj.isChannel && !obj.isButtonChannel && !obj.isActorChannel && !obj.isLogicChannel) {
            if (addIfMatchingString(obj.tag, searchString, obj)) return;
          }

          if (addIfMatchingString(obj.name, searchString, obj)) return;

          //   // Search for Channel Signal
          //   if (obj.treeViewModel.className == "SignalTreeViewModel") {
          //     if ((obj as Signal).type.name != "Ignored") {
          //       const signalName = (obj as Signal).knxFunctionText;
          //       addIfMatchingString(signalName, searchString, obj);
          //     }
          //     return;
          //   }

          //   // Search by Name
          //   if (addIfMatchingString(obj.name, searchString, obj)) return;

          //   // Search by Channel Name (For Button/Actor Channels)
          //   if (obj.isChannel || obj.isButtonChannel) {
          //     const channelName = (obj as Channel).knxChannelId;
          //     if (addIfMatchingString(channelName, searchString, obj)) return;
          //   }

          //   // Search By Node's Prefix
          //   const prefix = obj.treeViewModel.getTitlePrefix(); // can be type string or JSX.Element
          //   const prefixString = getStringValueFromJSXElement(prefix);
          //   if (addIfMatchingString(prefixString, searchString, obj)) return;

          //   // Search By Node's Postfix
          //   const postfix = obj.treeViewModel.getTitlePostfix(); // can be type string or JSX.Element
          //   const postfixString = getStringValueFromJSXElement(postfix);
          //   if (addIfMatchingString(postfixString, searchString, obj)) return;

          //   // Search By Node's FullTitle
          //   // (Valid for searching Channel Links shown as postfix if not found as postfix)
          //   const fullTitle = obj.treeViewModel.getFullTitle(); // can be type string or JSX.Element
          //   const fullTitleString = getStringValueFromJSXElement(fullTitle);
          //   if (addIfMatchingString(fullTitleString, searchString, obj)) return;
        }
      }

      function addIfMatchingString(queryStr: string | undefined, searchStr: string, treeObj: any): boolean {
        if (queryStr && queryStr.toLocaleLowerCase().indexOf(searchStr) >= 0) {
          nextSearchCollection.push(treeObj);
          return true;
        }
        return false;
      }

      function createSearchCollection() {
        // Adding Displayable Project Tree Items in Collection.
        // For now, the reference tree objects / connections / links are commented out in the below code
        // Focused on searching unique and original node object and not referential objects

        // Search for Building Structure
        // Search for Floors / Rooms / Consumers / Buttons / ButtonChannels   (Level1 - Level4)
        state.floors.forEach((floor) => {
          addToSearchCollection(floor);
          floor.rooms.forEach((room) => {
            addRoomHierarchy(room, addToSearchCollection);
          });
        });

        // Search for Cabinets
        // Cabinets / Actors / Fuse ELCBS / TerminalStrips / Channels  /// Linked Consumer (Level1 - Level3)
        state.cabinets.forEach((cabinet) => {
          addToSearchCollection(cabinet);
          cabinet.actors.forEach((actor) => {
            addToSearchCollection(actor);
            // actor.channels.sortBy("tag").forEach((channel) => {
            //   addToSearchCollection(channel);
            //   channel.signals.sortBy("tag").forEach((signal) => {
            //     addToSearchCollection(signal);
            //     // GroupAddress
            //   });
            // });
          });
          cabinet.elcbs.forEach((elcb) => {
            addToSearchCollection(elcb);
            elcb.fuses.forEach((fuse) => {
              addToSearchCollection(fuse);
              fuse.consumers.forEach((consumer) => {
                addToSearchCollection(consumer);
              });
            });
          });
          cabinet.terminalStrips.forEach((terminalStrip) => {
            addToSearchCollection(terminalStrip);
            // terminalStrip.terminals.forEach((terminal) => {
            //   addToSearchCollection(terminal);
            // });
          });
        });

        // Search for TemplateGroups
        // TemplateGroups / Template / Device Reference (Level1 - Level3)
        state.templateGroups.forEach((templateGroup) => {
          addToSearchCollection(templateGroup);
          templateGroup.templates.forEach((template) => {
            addToSearchCollection(template);
            // DeviceReference
          });
        });

        // Search for Group Addresses
        // address lvl1 / lvl2 / lvl3 (Level1 - Level3)
        state.addresses.forEach((addresslvl1) => {
          addToSearchCollection(addresslvl1);
          addresslvl1.children.forEach((addresslvl2) => {
            addToSearchCollection(addresslvl2);
            addresslvl2.children.forEach((addresslvl3) => {
              addToSearchCollection(addresslvl3);
              // BaseModelReference
            });
          });
        });

        // Search for Scene Groups
        // Scene / Connection (Level1 - Level2)
        state.sceneGroup.forEach((scene) => {
          addToSearchCollection(scene);
          // BaseModelReference
        });

        // Search for Documents
        // PageGroups / Page (Level1 - Level2)
        state.pageGroups.forEach((pageGroup) => {
          addToSearchCollection(pageGroup);
          pageGroup.pageGroups.forEach((innerPageGroup) => {
            addToSearchCollection(innerPageGroup);
          });
          pageGroup.pages.forEach((page) => {
            addToSearchCollection(page);
          });
          // Room
        });

        // Search for Topologies
        // // Line / Area / BaseModelReference (Level1 - Level3)
        // state.topologies.forEach((area) => {
        //   addToSearchCollection(area);
        //     area.treeViewModel.childCollection?.forEach((line) => {
        //       addToSearchCollection(line);
        //       line.treeViewModel.childCollection?.forEach((baseModelReference) => {
        //         addToSearchCollection(baseModelReference);
        //       });
        //     });
        // });
      }

      await showToast({ text: itx("treeSearch.searching", "Searching..."), keepVisible: true });
      createSearchCollection();

      hideToast();
    }

    // Sort by fullTag results in non-sequential search results while navigating through next/previous result
    // nextSearchCollection = sortArrayByProperty(nextSearchCollection, "fullTag");
    // Sort by fullTag is now done during hierarchy navigation itself

    nextSearchCollection.sort((a, b) => {
      const sA = a.className + a.simpleFullTag;
      const sB = b.className + b.simpleFullTag;
      if (sA < sB) return -1;
      if (sA > sB) return 1;

      return sA.localeCompare(sB);
    });

    searchCollection.current = nextSearchCollection;
    searchCollectionIndex.current = 0;
    if (nextSearchCollection.length > 0) {
      state.selectionManager.selectedObject = nextSearchCollection[0];
    }
    if (searchString != undefined) {
      searchExpression.current = searchString;
    }
    setUiChangeFlag(uiChangeFlag + 1);
  }

  function handleForwardSearch() {
    if (searchCollectionIndex.current != undefined && searchCollection.current) {
      searchCollectionIndex.current = (searchCollectionIndex.current + 1) % searchCollection.current.length;
      state.selectionManager.selectedObject = searchCollection.current[searchCollectionIndex.current];
    }
    setUiChangeFlag(uiChangeFlag + 1);
  }

  function handleBackwardSearch() {
    if (searchCollectionIndex.current != undefined && searchCollection.current) {
      searchCollectionIndex.current = searchCollectionIndex.current - 1;
      if (searchCollectionIndex.current < 0) {
        searchCollectionIndex.current = searchCollection.current.length - 1;
      }
      state.selectionManager.selectedObject = searchCollection.current[searchCollectionIndex.current];
    }
    setUiChangeFlag(uiChangeFlag + 1);
  }

  if (!state.treeSearchState.value) {
    return null;
  }

  return (
    <div style={{ marginBottom: "5px" }}>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0px 5px",
          margin: "0px 1px",
        }}
      >
        <input
          className="input"
          ref={refInput}
          key={"searchTree"} // no itx
          placeholder={state.translate("treeSearch.searchTree", "Search in Tree...")}
          defaultValue={""}
          style={{ width: "calc(100% - 60px)", margin: "3px 6px 5px 5px" }}
          id="searchTree"
          type="text"
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              if (refInput.current?.value.toLocaleLowerCase() !== searchExpression.current) {
                handleSearch();
              } else if (ev.shiftKey) {
                handleBackwardSearch();
              } else {
                handleForwardSearch();
              }
            }
          }}
        />
        <div
          className={"menu-item-wrapper dataline associatedIcon"} // no itx
          onClick={() => {
            if (refInput.current?.value.toLocaleLowerCase() !== searchExpression.current) {
              handleSearch();
            } else handleBackwardSearch();
          }}
          // MWE 14.05.2024:: Uncomment the following after translation fixes. Currently the translation strings are duplicated at places
          //   title={state.translate("treeSearch.prevMatch", "Find Previous (Shift + Enter)")}
          // MWE 17.05.2024:: Using the tooltip instead after translation fixes

          data-tooltip-id="tv-tooltip"
          data-tooltip-content={state.translate("treeSearch.prevMatch", "Find Previous (Shift + Enter)")}
          data-tooltip-place="bottom"
        >
          <i className="menuIcon las la-arrow-up" />
        </div>
        <div
          className={"menu-item-wrapper dataline associatedIcon"} // no itx
          onClick={() => {
            if (refInput.current?.value.toLocaleLowerCase() !== searchExpression.current) {
              handleSearch();
            } else handleForwardSearch();
          }}
          // MWE 14.05.2024:: Uncomment the following after translation fixes. Currently the translation strings are duplicated at places
          //   title={state.translate("treeSearch.nextMatch", "Find Next (Enter)")}
          // MWE 17.05.2024:: Using the tooltip instead after translation fixes

          data-tooltip-id="tv-tooltip"
          data-tooltip-content={state.translate("treeSearch.nextMatch", "Find Next (Enter)")}
          data-tooltip-place="bottom"
        >
          <i className="menuIcon las la-arrow-down" />
        </div>
      </div>

      {/* MWE 14.05.2024:: Uncomment the following after translation fixes. Currently the translation strings are duplicated at places */}
      {/* MWE 17.05.2024:: Fixed after recent translation commit */}

      {searchCollection.current.length > 0 && refInput.current && refInput.current?.value.length >= 2 && (
        <div style={{ fontStyle: "italic" }}>
          {state
            .translate("treeSearch.result", "Result: T1 of T2")
            .replace("T1", (searchCollectionIndex.current + 1).toString())
            .replace("T2", searchCollection.current.length.toString())}
        </div>
      )}
      {uiChangeFlag > 0 &&
        searchCollection.current.length == 0 &&
        refInput.current &&
        refInput.current?.value.length >= 2 && (
          <div style={{ fontStyle: "italic" }}>{state.translate("treeSearch.noResult", "No Result(s)")}</div>
        )}
      {refInput.current && refInput.current?.value.length < 2 && (
        <div style={{ fontStyle: "italic", color: "var(--error-color)" }}>
          {state.translate("treeSearch.minCharRequired", "Minimum 2 characters required")}
        </div>
      )}
      <div className="bottom-shadow" style={{ height: "8px" }}></div>
    </div>
  );
}
